/**
 * admin/capabilities — framework-agnostic RBAC role → capability map.
 *
 * Mirrors the backend capability gating (campaign #18, Track B). The back-office
 * UI uses this to (a) decide whether a user may enter the admin area at all and
 * (b) hide/disable individual actions a role can't perform. The backend remains
 * the source of truth and re-checks every mutation; this is purely a UX layer so
 * the client never offers an action that would 403.
 *
 * Roles (broad → narrow):
 *   - super_admin — everything, including destructive GDPR removals + role grants
 *   - admin       — manage users/flags, but not destructive removals or granting
 *                   privileged roles
 *   - support     — read users + audit, ban/unban + revoke sessions (no role/flag
 *                   changes, no removals)
 *   - read_only   — read-only access to every admin panel
 *   - user        — no admin access
 */

export const ADMIN_ROLES = [
  "super_admin",
  "admin",
  "support",
  // F2-C: recepción/caja (registra pagos/leads, ve métricas; NO gestiona usuarios).
  "staff",
  "read_only",
  "user",
] as const;

export type AdminRole = (typeof ADMIN_ROLES)[number];

/** Discrete capabilities the back-office UI gates on. */
export type AdminCapability =
  | "admin.access"
  | "users.read"
  | "users.setRole"
  | "users.ban"
  | "users.revokeSessions"
  // Restablecer contraseña desde el sistema (modelo demo-seed: el admin es la
  // única credencial aprovisionada; el resto se fija aquí). Backend: ManageUsers.
  | "users.setPassword"
  | "users.remove"
  // ── F2-C: gestión avanzada de usuarios ──
  | "users.impersonate"
  | "users.invite"
  | "users.bulk"
  | "users.guardians"
  | "audit.read"
  | "integrations.read"
  | "integrations.testEmail"
  | "flags.read"
  | "flags.write"
  | "metrics.read"
  // Mutar contenido de plataforma: temas, fuentes, plantillas y campañas de
  // email. Espejo de `Capability::ManageContent` en `auth/rbac.rs`, que desde el
  // endurecimiento SÍ gatea los routers /api/admin/{font,theme,email}. Faltaba
  // en este mapa, así que la UI ni siquiera podía expresar la restricción.
  | "content.write"
  // ── F1-B (plataforma academia) — espejo de `auth/rbac.rs` ──
  // Registrar pagos (caja). Rust: ManagePayments (staff + admin/super_admin).
  | "payments.manage"
  // CRM: leads/pipeline. Rust: ManageLeads (staff + support + admin/super_admin).
  | "leads.manage"
  // Import/export vía el kit Excel. Rust: ManageData (admin/super_admin).
  | "data.manage";

const READ_CAPS: AdminCapability[] = [
  "admin.access",
  "users.read",
  "audit.read",
  "integrations.read",
  "flags.read",
  "metrics.read",
];

const SUPPORT_CAPS: AdminCapability[] = [
  ...READ_CAPS,
  "users.ban",
  "users.revokeSessions",
  // Mismo nivel que ban/revoke: lo concede ManageUsers en el backend.
  "users.setPassword",
  // F2-C: ManageUsers permite suspender/reactivar en masa y gestionar
  // invitaciones/representantes (mismo nivel que ban).
  "users.bulk",
  "users.invite",
  "users.guardians",
  "integrations.testEmail",
  // F1-B: Support suma ManageLeads (atención = seguimiento comercial).
  "leads.manage",
  // Paridad Rust: `Support` tiene ManageContent.
  "content.write",
];

// F2-C: cambiar rol (incl. en masa) e impersonar exigen privilegio admin.
const ADMIN_CAPS: AdminCapability[] = [
  ...SUPPORT_CAPS,
  "users.setRole",
  "users.impersonate",
  "flags.write",
  // F1-B: Admin/SuperAdmin obtienen TODAS las capacidades (paridad Rust).
  "payments.manage",
  "data.manage",
];

const SUPER_ADMIN_CAPS: AdminCapability[] = [...ADMIN_CAPS, "users.remove"];

// F2-C: recepción/caja — entra al back-office; registra pagos y gestiona leads,
// ve métricas (paridad Rust: ManagePayments | ManageLeads | ViewMetrics).
const STAFF_CAPS: AdminCapability[] = [
  "admin.access",
  "metrics.read",
  "payments.manage",
  "leads.manage",
];

const CAPABILITIES_BY_ROLE: Record<AdminRole, AdminCapability[]> = {
  super_admin: SUPER_ADMIN_CAPS,
  admin: ADMIN_CAPS,
  support: SUPPORT_CAPS,
  staff: STAFF_CAPS,
  read_only: READ_CAPS,
  user: [],
};

function isAdminRole(role: string | null | undefined): role is AdminRole {
  return typeof role === "string" && (ADMIN_ROLES as readonly string[]).includes(role);
}

/** Narrows an arbitrary string (e.g. `session.user.role`) to a known role. */
export function toAdminRole(role: string | null | undefined): AdminRole {
  return isAdminRole(role) ? role : "user";
}

/** Returns the capability set for a role string (unknown → `user` → none). */
export function capabilitiesForRole(role: string | null | undefined): ReadonlySet<AdminCapability> {
  return new Set(CAPABILITIES_BY_ROLE[toAdminRole(role)]);
}

/** True when the role grants the given capability. */
export function roleCan(role: string | null | undefined, capability: AdminCapability): boolean {
  return capabilitiesForRole(role).has(capability);
}

/** True when the role may enter the admin back-office at all. */
export function isAdminCapable(role: string | null | undefined): boolean {
  return roleCan(role, "admin.access");
}

// ── Orden entre roles de plataforma (espejo de `auth/rbac.rs`) ──────────────
//
// NO hay un `rank` numérico a propósito: en Space, `support` y `staff` son
// INCOMPARABLES (cada uno tiene capacidades que el otro no), así que una escala
// lineal mentiría. El orden se deriva del propio mapa de capacidades, igual que
// en Rust (`outranks`): A domina a B si tiene TODAS las capacidades de B.
//
// Derivarlo del mapa —en vez de escribir una tabla aparte— es lo que mantiene
// los dos runtimes de acuerdo sin un tercer sitio que actualizar.

/** Capacidades que existen en AMBOS runtimes; las demás son solo de UI. */
function coreCaps(role: AdminRole): ReadonlySet<AdminCapability> {
  return capabilitiesForRole(role);
}

/** ¿`actor` tiene todas las capacidades de `target`? (dominio no estricto) */
function dominates(actor: AdminRole, target: AdminRole): boolean {
  const a = coreCaps(actor);
  return [...coreCaps(target)].every((c) => a.has(c));
}

/**
 * ¿Puede `actor` administrar a un usuario cuyo rol ACTUAL es `target`?
 * Estrictamente por encima, salvo `super_admin` sobre sus pares (si no, un
 * `super_admin` deshonesto sería intocable). Espejo de `can_administer`.
 */
export function canAdministerUser(
  actorRole: string | null | undefined,
  targetRole: string | null | undefined,
): boolean {
  const actor = toAdminRole(actorRole);
  const target = toAdminRole(targetRole);
  if (actor === "super_admin") return true;
  return dominates(actor, target) && !dominates(target, actor);
}

/**
 * Roles que `actorRole` puede CONCEDER.
 *
 * Sustituye a la antigua constante `ASSIGNABLE_ROLES = ADMIN_ROLES` (la lista
 * entera, para cualquiera). El nombre de una constante no obliga a decir
 * *asignable por quién*; una firma con parámetro sí. Espejo de `can_grant_role`:
 * nunca un rol con capacidades que el actor no tenga.
 */
export function assignableRolesFor(actorRole: string | null | undefined): readonly AdminRole[] {
  const actor = toAdminRole(actorRole);
  if (!roleCan(actor, "users.setRole")) return [];
  return ADMIN_ROLES.filter((target) => dominates(actor, target));
}
